import { useConversationMessageCacheHandler } from "#features/conversations/hooks/useConversationMessageCacheHandler.ts";
import { useConversationMessageStreamHandler } from "#features/conversations/hooks/useConversationMessageStreamHandler.ts";
import type { FileModel } from "#features/conversations/types/FileModel.ts";
import { ThinkingStepCodes, ThinkingStepStates } from "#features/conversations/types/ThinkingStep.ts";
import { useAiApiClient } from "#lib/AiApiClient.ts";
import { AiMutationKeys } from "#lib/AiQueryKeys.ts";
import { isAiSubdomain, sanitizePageContext } from "#lib/utils/pageContextSanitizer.ts";
import type { InjectedSourceWithSourceType } from "#types/injectedSource.ts";
import type { PageActionModel } from "#types/PageActionModel.ts";
import type { AssistantMode, ConnectorChannelType, CreateConversationMessageCommand, DisabledConnectorChannelType, FunctionName, LlmId, ResumeEntry, SkillTemplateSelection, UploadedConversationMessageFile } from "#types/types.ts";
import type { EventSourceMessage } from "@microsoft/fetch-event-source";
import { useMutation } from "@tanstack/react-query";
import { useRef } from "react";

export interface CreateConversationMessagePayload {
    conversationId: string;
    content: string;
    files: FileModel[];
    messageId: string;
    abortController: AbortController;
    additionalInstructions?: string;
    disableSearch: boolean;
    pageActions?: PageActionModel[];
    targetedReply?: string;
    context?: string;
    sources?: InjectedSourceWithSourceType[];
    promptFeature?: string;
    selectedSkills?: Record<string, SkillTemplateSelection>;
    llmId?: LlmId;
    productId: string;
    assistantMode?: AssistantMode;
    dataLlmEnrichment?: boolean;
    disabledChannels?: ConnectorChannelType[];
    resume?: ResumeEntry[];
    preApprovedTools?: FunctionName[];
    retry?: boolean;
    onMutate?: () => void;
    onError?: (error: Error) => void;
    onSettled?: () => void;
}

// Core's own channel-type enum is narrower than ours: UserRemoteMcp only labels remote-MCP citations and is
// never connectable, and the per-vendor remote-MCP channels have no core counterpart yet. Sending any of them
// gets the whole turn rejected, so drop them to keep the payload within the enum core accepts. Keying the
// blocklist off the generated types makes the compiler flag the next channel core can't parse.
const nonDisableableChannels: Record<Exclude<ConnectorChannelType, DisabledConnectorChannelType>, true> = {
    UserRemoteMcp: true,
    UserGranolaMcp: true,
    UserHubspotMcp: true,
    UserAsanaMcp: true,
    UserClickupMcp: true,
    UserLinearMcp: true,
    UserMiroMcp: true,
    UserIntercomMcp: true,
    UserMixpanelMcp: true,
    UserGongMcp: true,
    UserFigmaMcp: true,
    UserGithubMcp: true,
    UserAtlassianMcp: true,
    UserZoomMcp: true
};

const isDisableableChannel = (channel: ConnectorChannelType): channel is DisabledConnectorChannelType =>
    !(channel in nonDisableableChannels);

export const useCreateConversationMessageMutation = () => {
    const { createConversationMessage } = useAiApiClient();
    const {
        addUserMessage,
        addFileMessage,
        addTargetedReplyMessage,
        addAssistantMessage,
        updateMessage
    } = useConversationMessageCacheHandler();

    // RUN_FINISHED's metadata puts the persisted message id in this ref rather than straight in the cache:
    // handleClose renames the optimistic message once the native bridge has settled.
    const messageIdRef = useRef<string>("");
    const { handleOpen, handleClose, handleError, startNativeStreamAgent } =
        useConversationMessageStreamHandler(messageIdRef);

    return useMutation<void, Error, CreateConversationMessagePayload>({
        mutationKey: AiMutationKeys.CreateConversationMessage(),
        retry: 0,
        gcTime: Infinity,
        onMutate: async ({
            conversationId,
            messageId,
            content,
            files,
            retry,
            onMutate,
            pageActions,
            targetedReply,
            resume
        }) => {
            onMutate?.();

            if (retry) {
                updateMessage(conversationId, messageId, message => ({
                    ...message,
                    timestamp: new Date().toISOString(),
                    content: "",
                    role: "assistant",
                    sources: [],
                    searchResultUsers: [],
                    assistantStatus: "thinking",
                    contextMessage: "",
                    thinkingSteps: [
                        {
                            id: "thinking-1",
                            code: ThinkingStepCodes.thinking,
                            state: ThinkingStepStates.active,
                            sourceCount: 0,
                            sourceTitles: []
                        }
                    ]
                }));

                return;
            }

            // Decision-only post (empty content + resume entries): skip the user bubble, like the backend.
            const isDecisionOnly = !content && (resume?.length ?? 0) > 0;

            if (!isDecisionOnly) {
                addFileMessage(conversationId, files);
                addTargetedReplyMessage(conversationId, targetedReply);
                addUserMessage(conversationId, content);
            }

            addAssistantMessage(conversationId, messageId, files, pageActions);
        },
        mutationFn: async ({
            conversationId,
            content,
            messageId,
            files,
            abortController,
            context,
            sources,
            promptFeature,
            selectedSkills,
            llmId,
            productId,
            additionalInstructions,
            disableSearch,
            pageActions,
            targetedReply,
            assistantMode,
            dataLlmEnrichment,
            disabledChannels,
            resume,
            preApprovedTools
        }) => {
            // Core sends no message id for a turn that pauses before writing text; don't let it inherit the last turn's.
            messageIdRef.current = "";
            const nativeStream = startNativeStreamAgent(conversationId, messageId, { pageActions, promptFeature });
            // A transport error rejects createConversationMessage first and returns before settled is awaited.
            void nativeStream.settled.catch(() => {});
            // fetch-event-source resolves without calling onclose when the signal aborts; only a clean close settles the turn.
            let closed = false;
            // fetch-event-source fires neither onclose nor onerror on abort (the Stop button aborts this
            // signal), so settle the fed stream explicitly here — otherwise the agent run leaks on every
            // cancelled turn.
            abortController.signal.addEventListener("abort", () => nativeStream.error(new DOMException("Aborted", "AbortError")), { once: true });
            await createConversationMessage(
                {
                    conversationId,
                    content,
                    context,
                    sources: sources as CreateConversationMessageCommand["sources"],
                    additionalInstructions,
                    featureId: promptFeature,
                    selectedSkills,
                    llmId,
                    productId,
                    disableSearch,
                    assistantMode,
                    files: files.map<UploadedConversationMessageFile>(
                        file => ({ filePath: file.filePath! })
                    ),
                    pageContext: !isAiSubdomain() ? sanitizePageContext(16384, { dataLlmEnrichment }) : undefined,
                    pageActions,
                    targetedReply,
                    disabledChannels: disabledChannels?.filter(isDisableableChannel),
                    resume,
                    preApprovedTools
                },
                {
                    onopen: response => handleOpen(response),
                    onmessage: (event: EventSourceMessage) => {
                        // The native agent is the stream's only reader (R8.1).
                        nativeStream.feed(event);
                    },
                    onclose: () => {
                        closed = true;
                        nativeStream.close();
                    },
                    onerror: error => {
                        nativeStream.error(error);
                        throw error;
                    },
                    signal: abortController.signal
                }
            );

            if (!closed) {
                return;
            }

            // The native agent may still be draining buffered frames (its SDK is lazy-loaded). handleClose
            // renames the optimistic message id to the persisted one and completes every thinking step.
            // updateMessage matches on id, so closing first would drop a native update still in flight, or
            // leave a step it appends stuck active. A run error rejects here and reaches onError.
            await nativeStream.settled;
            handleClose(conversationId, messageId);
        },
        onError: (error, { conversationId, messageId, onError }) => {
            handleError(conversationId, messageId, error);
            onError?.(error);
        },
        onSettled: (_, __, { onSettled }) => {
            onSettled?.();
        }
    });
};
